import React, { useEffect, useState } from 'react';
import { Save, Globe, DollarSign, Shield, Bell, Key, Wallet } from 'lucide-react';
import { adminService } from '../../services/adminService';
import Toast from '../../components/Toast';
import { useTranslation } from '../../i18n/LocaleContext';

const AdminSettings = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({
    siteName: 'LikeUp',
    siteUrl: 'https://likeup.app',
    minDeposit: 100000,
    minWithdraw: 200000,
    withdrawFee: 0,
    maintenanceMode: false,
    emailNotifications: true,
    smsNotifications: false
  });
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [toast, setToast] = useState(null);
  const [tronAddress, setTronAddress] = useState('');
  const [savingChainPay, setSavingChainPay] = useState(false);

  useEffect(() => {
    adminService.getPaymentConfig()
      .then((res) => setTronAddress(res.tronAddress || ''))
      .catch(() => {});
  }, []);

  const handleSaveChainPay = async () => {
    const addr = tronAddress.trim();
    if (addr && !/^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(addr)) {
      setToast({ type: 'error', message: t('aSetInvalidAddress') });
      return;
    }
    setSavingChainPay(true);
    try {
      await adminService.savePaymentConfig(addr);
      setTronAddress(addr);
      setToast({ type: 'success', message: t('settingsSaved') });
    } catch (error) {
      setToast({ type: 'error', message: error.message });
    } finally {
      setSavingChainPay(false);
    }
  };

  const handleSave = () => {
    setToast({ type: 'success', message: t('settingsSaved') });
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!newPassword || newPassword.length < 6) {
      setToast({ type: 'error', message: t('passwordTooShort') });
      return;
    }

    if (newPassword !== confirmPassword) {
      setToast({ type: 'error', message: t('passwordMismatch') });
      return;
    }

    try {
      setChangingPassword(true);
      await adminService.changeAdminPassword(newPassword);
      setToast({ type: 'success', message: t('passwordChanged') });
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setToast({ type: 'error', message: error.message });
    } finally {
      setChangingPassword(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div>
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
      
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white">{t('aSetTitle')}</h1>
        <p className="text-gray-400 mt-1">{t('aSetSubtitle')}</p>
      </div>

      <div className="space-y-6">
        {/* Change Password */}
        <div className="bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-700">
          <div className="flex items-center gap-2 mb-4">
            <Key className="w-5 h-5 text-gray-400" />
            <h3 className="text-lg font-semibold text-white">{t('changePassword')}</h3>
          </div>
          
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('newPassword')}</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('passwordMinimum')}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('confirmPassword')}</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('passwordConfirm')}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>

            <button
              type="submit"
              disabled={changingPassword || !newPassword || !confirmPassword}
              className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Key className="w-4 h-4" />
              {changingPassword ? t('processing') : t('changePassword')}
            </button>
          </form>
        </div>
        {/* General Settings */}
        <div className="bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-700">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-5 h-5 text-gray-400" />
            <h3 className="text-lg font-semibold text-white">{t('aSetGeneral')}</h3>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('aSetSiteName')}</label>
              <input
                type="text"
                value={settings.siteName}
                onChange={(e) => setSettings({ ...settings, siteName: e.target.value })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('aSetSiteUrl')}</label>
              <input
                type="text"
                value={settings.siteUrl}
                onChange={(e) => setSettings({ ...settings, siteUrl: e.target.value })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
          </div>
        </div>

        {/* Payment Settings */}
        <div className="bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-700">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="w-5 h-5 text-gray-400" />
            <h3 className="text-lg font-semibold text-white">{t('aSetPayment')}</h3>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('aSetMinDeposit')}</label>
              <input
                type="number"
                value={settings.minDeposit}
                onChange={(e) => setSettings({ ...settings, minDeposit: parseInt(e.target.value) })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <p className="text-xs text-gray-400 mt-1">{formatCurrency(settings.minDeposit)}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('aSetMinWithdraw')}</label>
              <input
                type="number"
                value={settings.minWithdraw}
                onChange={(e) => setSettings({ ...settings, minWithdraw: parseInt(e.target.value) })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <p className="text-xs text-gray-400 mt-1">{formatCurrency(settings.minWithdraw)}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('aSetWithdrawFee')}</label>
              <input
                type="number"
                value={settings.withdrawFee}
                onChange={(e) => setSettings({ ...settings, withdrawFee: parseFloat(e.target.value) })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
          </div>
        </div>

        {/* Chain Payment Settings */}
        <div className="bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-700">
          <div className="flex items-center gap-2 mb-4">
            <Wallet className="w-5 h-5 text-gray-400" />
            <h3 className="text-lg font-semibold text-white">{t('aSetChainPay')}</h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('aSetCurrency')}</label>
              <input
                type="text"
                value="USDT-TRC20"
                readOnly
                className="w-full px-4 py-2 bg-gray-900 border border-gray-600 text-gray-400 rounded-lg focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('aSetTronAddress')}</label>
              <input
                type="text"
                value={tronAddress}
                onChange={(e) => setTronAddress(e.target.value)}
                placeholder="T..."
                className="w-full px-4 py-2 bg-gray-900 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <p className="text-xs text-gray-400 mt-1">{t('aSetChainPayHint')}</p>
            </div>

            <button
              onClick={handleSaveChainPay}
              disabled={savingChainPay}
              className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {savingChainPay ? t('processing') : t('aSetSave')}
            </button>
          </div>
        </div>

        {/* Security Settings */}
        <div className="bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-700">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-gray-400" />
            <h3 className="text-lg font-semibold text-white">{t('aSetSecurity')}</h3>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-white">{t('aSetMaintenance')}</p>
                <p className="text-sm text-gray-400">{t('aSetMaintenanceHint')}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.maintenanceMode}
                  onChange={(e) => setSettings({ ...settings, maintenanceMode: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-500/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-500"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-700">
          <div className="flex items-center gap-2 mb-4">
            <Bell className="w-5 h-5 text-gray-400" />
            <h3 className="text-lg font-semibold text-white">{t('aSetNotifications')}</h3>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-white">{t('aSetEmailNotif')}</p>
                <p className="text-sm text-gray-400">{t('aSetEmailNotifHint')}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.emailNotifications}
                  onChange={(e) => setSettings({ ...settings, emailNotifications: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-500/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-500"></div>
              </label>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-white">{t('aSetSmsNotif')}</p>
                <p className="text-sm text-gray-400">{t('aSetSmsNotifHint')}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.smsNotifications}
                  onChange={(e) => setSettings({ ...settings, smsNotifications: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-500/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-500"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-6 py-3 bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium"
          >
            <Save className="w-5 h-5" />
            {t('aSetSave')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
